import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { useBooleanState } from '@cian/react-utils';

import { type FC, useCallback, useState } from 'react';

import { type RemotenessTypes } from 'shared/types/RemotenessTypes';
import { type TransportTypes } from 'shared/types/TransportTypes';

import { Layout } from '../Layout';
import { NoImage } from '../NoImage';
import { SliderProgressBar } from '../SliderProgressBar';

import { firstPaintOptions, mediaSettings } from './config';
import { type BadgesTypes } from './internal/components/Badges';
import { Slide } from './internal/components/Slide';

import styles from './Carousel.css';

type TransportType = {
  id: number;
  name: string;
  iconColor?: string | null;
  remoteness?: string | null;
  remotenessType?: RemotenessTypes | null;
  iconType?: TransportTypes | null;
};

export type CarouselBanner = {
  id: number;
  publicUrl: string;
  badges: BadgesTypes[];
  imageUrl: string;
  title: string;
  disclaimer: string | null;
  label: string | null;
  transports: TransportType[];
  logoUrl: string | null;
  hasCommercialProperty: boolean;
};

type CarouselProps = {
  banners: CarouselBanner[];
  testId?: string;
  interval: number;
};

export const Carousel: FC<CarouselProps> = ({ banners, testId, interval }) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { state: paused, setTrue: setPaused, setFalse: setNotPaused } = useBooleanState(false);

  const handleAnimationEnd = useCallback(
    () => setCurrentSlide(prev => (prev < banners.length ? prev + 1 : 1)),
    [banners.length],
  );

  if (!banners.length) {
    return (
      <div className={styles['carousel']}>
        <NoImage />
      </div>
    );
  }

  return (
    <section className={styles['carousel']} data-testid={testId}>
      <NewbuildingCarousel
        defaultCurrentSlide={currentSlide}
        firstPaintOptions={firstPaintOptions}
        media={mediaSettings}
        slides={banners.map(banner => (
          <Slide
            badges={banner.badges}
            disclaimer={banner.disclaimer}
            hasCommercialProperty={banner.hasCommercialProperty}
            imageUrl={banner.imageUrl}
            key={banner.id}
            label={banner.label}
            logoUrl={banner.logoUrl}
            publicUrl={banner.publicUrl}
            testId={`main-carousel-slide-${banner.id}`}
            title={banner.title}
            transports={banner.transports}
          />
        ))}
        onSlideChange={({ currentSlide: slideIndex }) => setCurrentSlide(slideIndex)}
      />

      {banners.length > 1 && (
        <div className={styles['progress-bar']}>
          <Layout>
            <div className={styles['progress-bar__wrapper']}>
              <SliderProgressBar
                activeIndex={currentSlide - 1}
                count={banners.length}
                duration={interval}
                paused={paused}
                onAnimationEnd={handleAnimationEnd}
                onDotClick={index => setCurrentSlide(index + 1)}
                onProgressBarHover={setPaused}
                onProgressBarLeave={setNotPaused}
              />
            </div>
          </Layout>
        </div>
      )}
    </section>
  );
};
